import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Shield, Code2, Search, Brain, Upload, ArrowRight, CheckCircle } from 'lucide-react';
import './LandingPage.css';

const features = [
  { icon: Upload, title: 'Smart ZIP Upload', desc: 'Drag & drop ZIP files up to 2GB. Instant extraction with progress tracking.' },
  { icon: Code2, title: 'VS Code Editor', desc: 'Monaco editor with syntax highlighting, line numbers, multi-tab support.' },
  { icon: Brain, title: 'AI Code Analysis', desc: 'Get instant explanations, bug detection and improvement suggestions.' },
  { icon: Search, title: 'Full-Text Search', desc: 'Search across all files and content in your uploaded projects.' },
  { icon: Shield, title: 'OTP Login', desc: 'Secure passwordless login via email OTP — no password needed.' },
  { icon: Zap, title: 'Instant Preview', desc: 'Live preview for HTML files. Real-time code rendering.' },
];

const plans = [
  { name: 'Free', price: '₹0', limit: '25MB', uploads: '5/mo', ai: '3 requests', color: '#7a7a9d' },
  { name: 'Basic', price: '₹299', limit: '500MB', uploads: '50/mo', ai: '50 requests', color: '#00a3ff' },
  { name: 'Pro', price: '₹999', limit: '2GB', uploads: '200/mo', ai: '200 requests', color: '#7c3aed' },
  { name: 'Premium', price: '₹2499', limit: 'Unlimited', uploads: 'Unlimited', ai: 'Unlimited', color: '#00ff88', popular: true },
];

export default function LandingPage() {
  return (
    <div className="landing">
      {/* Nav */}
      <nav className="landing-nav">
        <div className="nav-logo">
          <span className="logo-icon">⚡</span>
          <span>Zip2Extract <span className="logo-ai">AI</span></span>
        </div>
        <div className="nav-links">
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <Link to="/login" className="btn btn-outline" style={{ padding: '8px 16px', fontSize: '13px' }}>Login</Link>
          <Link to="/register" className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '13px' }}>Get Started</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-glow" />
        <div className="hero-content">
          <div className="hero-badge">🚀 AI-Powered Code Explorer</div>
          <h1>Explore Code Like<br /><span className="gradient-text">Never Before</span></h1>
          <p>Upload ZIP files, explore with a VS Code-like editor, and use AI to understand any codebase instantly.</p>
          <div className="hero-cta">
            <Link to="/register" className="btn btn-primary" style={{ padding: '14px 28px', fontSize: '15px' }}>
              Start for Free <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="btn btn-outline" style={{ padding: '14px 28px', fontSize: '15px' }}>
              Login with OTP
            </Link>
          </div>
          <div className="hero-stats">
            <div className="stat"><span>25MB</span><p>Free upload</p></div>
            <div className="stat-divider" />
            <div className="stat"><span>20+</span><p>Features</p></div>
            <div className="stat-divider" />
            <div className="stat"><span>AI</span><p>Powered</p></div>
          </div>
        </div>
        {/* Code Preview Visual */}
        <div className="hero-visual">
          <div className="editor-mockup">
            <div className="editor-titlebar">
              <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
              <span className="editor-filename">index.js</span>
            </div>
            <div className="editor-body">
              <pre>{`const express = require('express');
<span class="kw">const</span> app = express();

app.<span class="fn">get</span>(<span class="str">'/api/hello'</span>, (req, res) => {
  res.<span class="fn">json</span>({ 
    message: <span class="str">'Hello World!'</span>,
    ai: <span class="kw">true</span>
  });
});

app.<span class="fn">listen</span>(<span class="num">3000</span>);`}</pre>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="features-section">
        <h2>Everything You Need to<br /><span className="gradient-text">Understand Code</span></h2>
        <p className="section-sub">From extraction to AI analysis — all in one platform</p>
        <div className="features-grid">
          {features.map((f, i) => (
            <div className="feature-card" key={i}>
              <div className="feature-icon"><f.icon size={22} /></div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="pricing-section">
        <h2>Simple, <span className="gradient-text">Transparent Pricing</span></h2>
        <p className="section-sub">Start free. Upgrade as you grow.</p>
        <div className="pricing-grid">
          {plans.map((p, i) => (
            <div className={`pricing-card ${p.popular ? 'popular' : ''}`} key={i}>
              {p.popular && <div className="popular-badge">Most Popular</div>}
              <h3 style={{ color: p.color }}>{p.name}</h3>
              <div className="price">{p.price}<span>/month</span></div>
              <ul>
                <li><CheckCircle size={14} />{p.limit} upload limit</li>
                <li><CheckCircle size={14} />{p.uploads} uploads</li>
                <li><CheckCircle size={14} />{p.ai}</li>
                <li><CheckCircle size={14} />VS Code editor</li>
              </ul>
              <Link to="/register" className="btn" style={{ background: p.color, color: p.name === 'Premium' ? '#000' : '#fff', width: '100%', justifyContent: 'center', marginTop: 'auto' }}>
                {p.name === 'Free' ? 'Start Free' : `Get ${p.name}`}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-logo">⚡ Zip2Extract AI</div>
        <p>© 2024 Zip2Extract AI. All rights reserved.</p>
      </footer>
    </div>
  );
}
